import React, { forwardRef, useEffect, useId, useMemo, useState } from "react";
import { FaInfoCircle, FaSpinner, FaExclamationTriangle } from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { Prompt } from "react-router-dom";
import DatePicker from "react-datepicker";
import Footer from "../../components/footer";
import FooterButton from "../../components/footerButton";
import PickDate from "../../components/common/datePicker";
import WarningModal from "../../components/clearWarningModal";
import GetFunctionalChecklist from "../../hooks/getFunctionalChecklist";
import Grade from "../../components/common/Grade";
import ScoringGuidelines from "../../components/common/ScoringGuidelines";
import AddFunctionalChecklistScore from "../../hooks/addFunctionalChecklistScore";
import GetFunctionalSummary from "../../hooks/getFunctionalSummary";
import AddContributorScore from "../../hooks/addContributorScore";
import GetPatient from "../../hooks/getPatient";
import { Bounce, toast } from "react-toastify";
import "react-toastify/ReactToastify.css";
import { ClipLoader } from "react-spinners";
import {
  emptyScoreItem,
  isAnswered,
  toScoresPayload,
} from "../../utils/scoreState";
import { scaleResolverFor } from "../../utils/scoringScale";
import { toAssessmentDatePayload } from "../../utils/helper";
const Item1 = ({ isOpen, setIsOpen, onClear, onClearConfirm }) => (
  <>
    <button
      className="btn btn_danger px-5 mt-3 mt-sm-0"
      onClick={() => {
        onClear();
        setIsOpen(true);
      }}
      data-toggle="modal"
      data-target="#exampleModal"
    >
      <small>CLEAR</small>
    </button>
    <WarningModal
      setIsOpen={setIsOpen}
      isOpen={isOpen}
      onConfirm={onClearConfirm}
    />
  </>
);

const Item2 = ({
  props,
  date,
  handleSubmit,
  isPending,
  scores,
  questions,
  onSaveForLater,
  onReview,
}) => {
  const handleReview = () => {
    onReview(); // Allow navigation
    localStorage.removeItem("documentId");
    props.history.push({
      pathname: `/hcp/patient/function_review_entry`,
      state: { date, scores: scores, questions: questions },
    });
  };

  const handleSaveForLater = () => {
    onSaveForLater(); // Allow navigation
    handleSubmit();
  };

  return (
    <div className="flex-grow-1 flex-grow-sm-0 d-flex justify-content-between justify-content-sm-end">
      {isPending && (
        <button className="btn btn_outline_light px-3 mx-0 mx-md-3 d-flex justify-content-between">
          {isPending && (
            <ClipLoader
              size={20}
              isLoading={true}
              className="mr-2"
              color="#fff"
            />
          )}

          <small> SAVE FOR LATER</small>
        </button>
      )}
      {!isPending && (
        <button
          className="btn btn_outline_light px-3 mx-0 mx-md-3 "
          onClick={handleSaveForLater}
        >
          <small> SAVE FOR LATER</small>
        </button>
      )}
      <button onClick={handleReview} className="btn btn_light pl-4 pr-2 ml-3">
        <small className="mx-3">REVIEW</small>
        <FiChevronRight size={20} />
      </button>
    </div>
  );
};

const AssessmentDateInput = forwardRef(({ value, onClick }, ref) => (
  <button
    type="button"
    className={`d-flex align-items-center justify-content-center w-100 px-3 py-2 rounded ${
      value ? "datePicker-active" : "datePicker"
    }`}
    style={{ height: "42px", backgroundColor: "#fff", position: "relative" }}
    onClick={onClick}
    ref={ref}
  >
    <small className={value ? "text_primary" : "text-muted"}>
      {value || "Choose Date"}
    </small>
    <i
      className="fa fa-calendar text_primary"
      style={{ position: "absolute", right: "12px" }}
    />
  </button>
));

const FunctionalScore = (props) => {
  const [date, setDate] = useState(
    props.location.state ? props.location.state.date : null
  );
  const [chosenDate, setChosenDate] = useState(
    props.location.state ? props.location.state.date : null
  );
  const [comment, setComment] = useState("");
  const [currentItem, setCurrentItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));

  // Navigation confirmation states
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [hasDataBeenEntered, setHasDataBeenEntered] = useState(false);

  const [currentUuid, setCurrentUuid] = useState(0);
  const [questions, setQuestions] = useState([]);
  const {
    data: addResponse,
    mutate,
    isPending: addIsPending,
    error: addError,
    isSuccess: addSuccess,
  } = AddFunctionalChecklistScore();
  const {
    data: dataContributor,
    mutate: mutateContributorScore,
    error: contributorError,
    isPending,
    isSuccess,
  } = AddContributorScore();
  const { data, isLoading, error } = GetFunctionalChecklist();

  // Patients can have more than one checklist assigned; the active one is
  // the one currently in use for scoring, falling back to the first entry
  // if none is flagged active.
  const activeChecklist = useMemo(
    () => data?.data?.find((item) => item.active) ?? data?.data?.[0],
    [data]
  );

  const headings =
    data && !isLoading && activeChecklist ? activeChecklist.headings : [];

  // Each row is validated against its own heading's scale before it is sent:
  // the scores list is flat, but the headings it spans may be scored on
  // different ranges.
  const resolveScale = scaleResolverFor(headings);

  const [scores, setScores] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const { data: PatientList } = GetPatient();
  const [patientChecklist, setPatientChecklist] = useState(null);
  const checklistId = useId();
  const patient = localStorage.getItem("patient");
  const collaborator = JSON.parse(localStorage.getItem("user"));

  // Check if coming from review page
  const fromReview = props.location.state?.fromReview;
  const reviewScores = props.location.state?.scores;
  const reviewHeadingsData = props.location.state?.headingsData;
  const reviewQuestions = props.location.state?.questions;
  const handleChange = ({ target }) => {
    setComment(target.value);
  };
  useEffect(() => {
    const modal = document.getElementById("commentModal");
    modal.addEventListener("hidden.bs.modal", handleCloseModal);
    return () => {
      modal.removeEventListener("hidden.bs.modal", handleCloseModal);
    };
  }, []);
  useEffect(() => {
    if (date && data) {
      let scoreData = [];

      // If coming from review page, use the review scores
      if (fromReview && reviewScores) {
        scoreData = reviewScores.scores;
      } else {
        // Default initialization for new entries: every item starts unanswered
        // (score null, notApplicable false) so nothing is preselected.
        activeChecklist?.headings.map((item) => {
          item.subheadings.map((subheading) => {
            scoreData.push(emptyScoreItem(subheading.id));
          });
        });
      }

      const functionalChecklistTitle = activeChecklist?.name;

      const newScores = {
        providerId: user?.organizations[0].careProvider.id,
        // Wall-clock, not a UTC instant: the assessment date is the day the
        // clinician saw the patient, and toISOString() let the server's zone
        // roll it onto the previous day.
        assessmentDate: toAssessmentDatePayload(date),
        patientId: patient,
        status: fromReview && reviewScores ? reviewScores.status : "DRAFT",
        scores: scoreData,
      };

      setScores(newScores);
    }
  }, [date, data, activeChecklist, fromReview, reviewScores]);
  useEffect(() => {
    if (PatientList && PatientList.length > 0) {
      const selPatient = PatientList.filter(
        (item) => item.pomme_id == patient
      )[0];
      setPatientChecklist(selPatient);
    }
  }, [PatientList]);

  // Initialize questions from API data
  useEffect(() => {
    if (activeChecklist?.questions) {
      const apiQuestions = [...activeChecklist.questions]
        .sort((a, b) => a.order - b.order)
        .map((q) => ({ id: q.id, question: q.text, answer: "" }));
      setQuestions(apiQuestions);
    }
  }, [activeChecklist]);

  // Restore questions when coming back from review page
  useEffect(() => {
    if (fromReview && reviewQuestions) {
      setQuestions(reviewQuestions.map((q) => ({ ...q, isEdit: undefined })));
    }
  }, [fromReview, reviewQuestions]);
  useEffect(() => {
    if (isSuccess || addSuccess) {
      setAllowNavigation(true); // Allow navigation after successful save
      toast.success(
        <p className="text-center">
          Your unfinished document <br /> will be saved in
          <br /> OUTSTANDING DOCUMENTS.
        </p>,
        {
          position: "top-right",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: "bg_secondary",
          theme: "colored",
          transition: Bounce,
        }
      );

      // Navigate to outstanding documents page after successful save
      setTimeout(() => {
        props.history.push({
          pathname: "/hcp/outstanding_documents",
          state: { preventBrowserBack: true },
        });
      }, 1000); // Wait 2 seconds to allow user to see the toast
    }
  }, [addSuccess, isSuccess, props.history]);

  // Track when data has been entered
  useEffect(() => {
    const hasScoreData =
      scores &&
      scores.scores &&
      scores.scores.some(
        (score) => isAnswered(score, resolveScale(score.subheadingId)) || score.comment
      );

    const hasQuestionData = questions.some((q) => q.answer.trim() !== "");

    if (hasScoreData || hasQuestionData) {
      setHasDataBeenEntered(true);
    }
  }, [scores, questions]);

  // Navigation blocking function
  const handleNavigationAttempt = (location) => {
    if (allowNavigation || !hasDataBeenEntered) {
      return true; // Allow navigation
    }

    // Allow navigation to review page without warning
    if (location.pathname === "/hcp/patient/function_review_entry") {
      return true;
    }

    setPendingLocation(location);
    setShowNavigationModal(true);
    return false; // Block navigation
  };

  // Confirm navigation and lose data
  const handleConfirmNavigation = () => {
    setAllowNavigation(true);
    setShowNavigationModal(false);

    // Navigate to the intended location
    if (pendingLocation) {
      props.history.push(pendingLocation.pathname);
    }
  };

  // Cancel navigation
  const handleCancelNavigation = () => {
    setShowNavigationModal(false);
    setPendingLocation(null);
  };

  // Handle footer button actions to allow navigation
  const handleClearAction = () => {
    setAllowNavigation(true);
  };

  const handleSaveForLaterAction = () => {
    setAllowNavigation(true);
  };

  const handleReviewAction = () => {
    setAllowNavigation(true);
  };

  // Function to clear all scores and comments
  const handleClearAllScores = () => {
    setScores((prevData) => ({
      ...prevData,
      scores: prevData.scores.map((item) => emptyScoreItem(item.subheadingId)),
    }));
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => ({ ...q, answer: "" }))
    );
    setHasDataBeenEntered(false); // Reset data entered flag
    setAllowNavigation(true); // Allow navigation after clearing
  };
  const handleShowModal = (heading, subheading) => {
    const subItem = scores.scores?.find(
      (subItem) => subItem.subheadingId === subheading
    );

    setCurrentItem({ heading, subheading });
    setComment(subItem?.comment || "");
    setShowModal(true);
    window.$("#commentModal").modal("show");
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setComment("");
    setCurrentItem(null);
  };
  // A score and N/A are mutually exclusive, so picking one always clears the
  // other.
  const handleGrade = (subheadingId, gradeValue, notApplicable = false) => {
    setScores((prevData) => ({
      ...prevData,
      scores: prevData.scores.map((item) =>
        item.subheadingId === subheadingId
          ? {
              ...item,
              score: notApplicable ? null : gradeValue,
              notApplicable,
            }
          : item
      ),
    }));
  };
  const handleAddComment = () => {
    setScores((prevData) => ({
      ...prevData,
      scores: prevData.scores.map((item) =>
        item.subheadingId === currentItem.subheading
          ? {
              ...item,
              comment,
            }
          : item
      ),
    }));

    window.$("#commentModal").modal("hide");
    handleCloseModal();
  };

  const handleQuestionAnswerChange = (questionId, answer) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => (q.id === questionId ? { ...q, answer } : q))
    );
  };
  const handleSubmit = () => {
    const payload = {
      ...scores,
      scores: toScoresPayload(scores.scores, resolveScale),
      questionAnswers: questions
        .filter((q) => q.answer.trim() !== "")
        .map((q) => ({
          id: q.answerId || "",
          questionId: q.id,
          answer: q.answer,
        })),
    };
    mutate(payload);
  };
  // Add function to handle overlay positioning
  const handleOverlayPositioning = () => {
    const containers = document.querySelectorAll(".icon-container");
    containers.forEach((container) => {
      const rect = container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const overlayWidth = 300; // Width of the overlay
      const spaceOnRight = viewportWidth - rect.right;

      // Remove existing positioning classes
      container.classList.remove("position-left", "position-right");

      // Add appropriate positioning class
      if (spaceOnRight < overlayWidth + 20) {
        container.classList.add("position-left");
      } else {
        container.classList.add("position-right");
      }
    });
  };

  // Add useEffect for overlay positioning
  useEffect(() => {
    // Initial positioning with delay to ensure DOM is rendered
    const timer = setTimeout(() => {
      handleOverlayPositioning();
    }, 100);

    // Handle window resize
    const handleResize = () => {
      handleOverlayPositioning();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [data, date]); // Re-run when data or date changes

  return (
    <>
      {hasDataBeenEntered && (
        <Prompt when={!allowNavigation} message={handleNavigationAttempt} />
      )}
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body mb-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center">
              <h5 className="font-weight-lighter mb-3 mb-sm-3 mb-md-0 mb-lg-0">
                HCP: ENTER Functional Scores
              </h5>
              {date && !isLoading && headings.length > 0 && (
                <div className="media align-items-center">
                  <h6 className="mb-0">
                    <small>Assessment Date: </small>
                  </h6>
                  <div className="mb-4 ml-2">
                    <div className="icon-container position-relative">
                      <sup>
                        <FaInfoCircle />
                      </sup>

                      <div className="overlay shadow rounded w-236">
                        <div
                          className="my-2 pl-2"
                          style={{ width: "100%", height: "100%" }}
                        >
                          <p>
                            The Assessment Date is the date that you SAW or
                            ASSESSED the patient. It is not always the date that
                            you enter the data.
                          </p>
                          <p>
                            eg. <br />
                            if you saw the patient on 10 Jan 2022 and you are
                            entering the data on 10 Jan 2022, select 10 Jan 2022
                            as the ASSESSMENT DATE.
                          </p>
                          <p>
                            if you saw the patient on 10 Jan 2022 and you are
                            entering the data on 12 Jan 2022, select 10 Jan 2022
                            as the ASSESSMENT DATE.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="media-body ml-2 w-186">
                    <PickDate date={date} setDate={setDate} maxDate={new Date()}/>
                  </div>
                </div>
              )}
            </div>
            <hr />
            {/* Show skeleton while loading */}
            {isLoading && (
              <div className="noScroll h-90 verticalScroll pb-17">
                <div className="p-4">
                  {/* Skeleton for headings */}
                  {[1].map((index) => (
                    <div key={index} className="mb-4">
                      {/* Skeleton for accordion header */}
                      <div
                        className="bg-light rounded p-3 mb-2"
                        style={{ height: "45px" }}
                      >
                        <div
                          className="bg-secondary rounded"
                          style={{
                            height: "16px",
                            width: "60%",
                            opacity: 0.3,
                          }}
                        ></div>
                      </div>
                      {/* Skeleton for accordion content */}
                      <div className="pl-3">
                        {[1, 2, 3, 4].map((subIndex) => (
                          <div
                            key={subIndex}
                            className="row align-items-center my-3"
                          >
                            <div className="col-lg-4 col-md-4 col-sm-4">
                              <div className="bg-light p-3">
                                <div
                                  className="bg-secondary rounded"
                                  style={{
                                    height: "14px",
                                    width: "80%",
                                    opacity: 0.3,
                                  }}
                                ></div>
                              </div>
                            </div>
                            <div className="col-lg-8 col-md-8 col-sm-8">
                              <div className="d-flex align-items-center">
                                {/* Skeleton for radio buttons */}
                                {[1, 2, 3, 4, 5].map((radioIndex) => (
                                  <div key={radioIndex} className="mr-3">
                                    <div
                                      className="bg-secondary rounded-circle"
                                      style={{
                                        height: "20px",
                                        width: "20px",
                                        opacity: 0.3,
                                      }}
                                    ></div>
                                  </div>
                                ))}
                                {/* Skeleton for comment button */}
                                <div
                                  className="bg-secondary rounded-pill ml-3"
                                  style={{
                                    height: "30px",
                                    width: "100px",
                                    opacity: 0.3,
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Show no checklist message when not loading and no headings */}
            {!isLoading && headings.length === 0 && (
              <div className="noScroll h-90 verticalScroll pb-17">
                <div className="d-flex flex-column justify-content-center align-items-center h-100 text-center">
                  <div className="p-4">
                    <h5 className="text-muted mb-3">No Checklist Available</h5>
                    <p className="text-muted">
                      There is no functional checklist configured for this
                      patient.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Show content when not loading, have headings, and date is selected */}
            {!isLoading && headings.length > 0 && date && chosenDate && (
              <div className="noScroll h-90">
                <div className="col-md-4 px-0 pt_1 pb-17">
                  <label
                    htmlFor=""
                    className="small-text border w-358 pl-3 h-35 align-content-center mb-0 rounded-5"
                  >
                    Functional Checklist Title:
                    <span className="ml-3">
                      {/* {patientChecklist &&
                        `${patientChecklist.lastname}, ${patientChecklist.firstname} - Custom`} */}
                      {activeChecklist?.name}
                    </span>
                  </label>
                </div>
                <div className="noScroll h-90 verticalScroll pb-17">
                  <Accordion
                    allowZeroExpanded
                    allowMultipleExpanded={false}
                    preExpanded={[currentUuid]}
                    className="mb-4 py-0"
                  >
                    {headings &&
                      headings.map((heading, i) => (
                        <div key={i}>
                          <div className="chevron" />
                          <AccordionItem
                            className="mb-2"
                            uuid={i}
                            dangerouslySetExpanded={
                              currentUuid === i ? true : false
                            }
                          >
                            <AccordionItemHeading
                              className="accordion__primary"
                              onClick={() => {
                                setCurrentUuid(i === currentUuid ? null : i);
                              }}
                            >
                              <AccordionItemButton
                                style={{
                                  height: 45,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <small>{heading.name}</small>
                              </AccordionItemButton>
                            </AccordionItemHeading>
                            <AccordionItemPanel>
                              <div className="d-flex my-2">
                                <div className="text_small">
                                  <span className="text_primary font-weight-bold">
                                    Start:
                                  </span>{" "}
                                  Choose score that represents functioning for
                                  each item
                                </div>
                                <div className="d-flex align-items-center flex-fill justify-content-center">
                                  &nbsp;&nbsp;
                                  <small className="mr-2 text_small">
                                    Scoring Guidelines
                                  </small>
                                  <div className="icon-container position-relative">
                                    <sup>
                                      <FaInfoCircle />
                                    </sup>

                                    <div className="overlay shadow rounded w-270">
                                      <div
                                        className="my-2 px-2 text-wrap"
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                        }}
                                      >
                                        <ScoringGuidelines
                                          scale={heading.scoringScale}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {heading.subheadings.map((item, key) => (
                                <div
                                  key={key}
                                  className="row align-items-center"
                                >
                                  <div className="col-lg-4 col-md-4 col-sm-4 mb-1 mb-sm-0 mb-md-0 md-lg-0">
                                    <div
                                      className={`d-flex justify-content-between ${
                                        key % 2 === 0 ? `bg_light` : ""
                                      } p-3`}
                                    >
                                      <small>{item.name}</small>
                                      <div />
                                      <small className="d-none">
                                        <FaInfoCircle size={9} />
                                      </small>
                                    </div>
                                  </div>
                                  <div className="col-lg-8 col-md-8 col-sm-8">
                                    <div className="row align-items-center pr-3 px-md-2 d-flex flex-wrap mx-1">
                                      <fieldset
                                        className="row col-md justify-content-sm-start justify-content-between"
                                        id={"group" + key + i}
                                      >
                                        <Grade
                                          handleGrade={handleGrade}
                                          item={item.id}
                                          scale={heading.scoringScale}
                                          isEdit={true}
                                          group={"group" + key + i}
                                          score={
                                            scores.scores?.find(
                                              (scoreItem) =>
                                                scoreItem.subheadingId ===
                                                item.id
                                            )?.score
                                          }
                                          notApplicable={
                                            scores.scores?.find(
                                              (scoreItem) =>
                                                scoreItem.subheadingId ===
                                                item.id
                                            )?.notApplicable === true
                                          }
                                        />
                                      </fieldset>
                                      {/* <div>
                                      <input
                                        type="text"
                                        placeholder="Add Comments"
                                        value={item.comment}
                                        onChange={(e) =>
                                          handleAddComment(
                                            heading.id,
                                            item.id,
                                            e.target.value
                                          )
                                        }
                                        className="func-score-comment-input"
                                      />
                                    </div> */}
                                      <a
                                        onClick={() =>
                                          handleShowModal(heading.id, item.id)
                                        }
                                        className={`hover_0 mx-2 mx-sm-0 mx-md-0 mx-lg-3 mt-2 ${
                                          scores.scores?.find(
                                            (scoreItem) =>
                                              scoreItem.subheadingId === item.id
                                          )?.comment
                                            ? "border_primary bg_light_primary"
                                            : "border_success"
                                        } rounded-pill text-center px-4 px-sm-2 py_1 col-xl-3 col-md-4 mt-sm-2 my-md-3 cursor-pointer`}
                                      >
                                        <small>
                                          {scores.scores?.find(
                                            (scoreItem) =>
                                              scoreItem.subheadingId === item.id
                                          )?.comment
                                            ? scores.scores.find(
                                                (scoreItem) =>
                                                  scoreItem.subheadingId ===
                                                  item.id
                                              )?.comment.length > 12
                                              ? scores.scores
                                                  .find(
                                                    (scoreItem) =>
                                                      scoreItem.subheadingId ===
                                                      item.id
                                                  )
                                                  ?.comment.substring(0, 12) +
                                                "..."
                                              : scores.scores.find(
                                                  (scoreItem) =>
                                                    scoreItem.subheadingId ===
                                                    item.id
                                                )?.comment
                                            : "Add Comment"}
                                        </small>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {headings.length > i + 1 && (
                                <div className="d-flex justify-content-end mt-4 mb-3">
                                  <button
                                    className="btn btn-outline-primary w-99 mr-3 rounded "
                                    onClick={() => {
                                      setCurrentUuid(currentUuid + 1);
                                    }}
                                  >
                                    <small>Next</small>
                                  </button>
                                </div>
                              )}
                            </AccordionItemPanel>
                          </AccordionItem>
                        </div>
                      ))}

                    {/* Questions Accordion Item */}
                    {questions.length > 0 && <div>
                      <div className="chevron" />
                      <AccordionItem
                        className="mb-2"
                        uuid="questions"
                        dangerouslySetExpanded={
                          currentUuid === "questions" ? true : false
                        }
                      >
                        <AccordionItemHeading
                          className="accordion__primary"
                          onClick={() => {
                            setCurrentUuid(
                              currentUuid === "questions" ? null : "questions"
                            );
                          }}
                        >
                          <AccordionItemButton
                            style={{
                              height: 45,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <small>Questions</small>
                          </AccordionItemButton>
                        </AccordionItemHeading>
                        <AccordionItemPanel>
                          {questions.map((question, key) => (
                            <div key={question.id} className="mb-3">
                              {/* Question Row */}
                              <div className="row align-items-center">
                                <div className="col-12">
                                  <div className="d-flex justify-content-between p-3">
                                    <small className="font-weight-bold">
                                      {question.question}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              {/* Textarea Row */}
                              <div className="row mt-2">
                                <div className="col-12">
                                  <div className="px-3">
                                    <textarea
                                      className="form-control"
                                      rows="3"
                                      placeholder="Enter your answer here..."
                                      value={question.answer}
                                      onChange={(e) =>
                                        handleQuestionAnswerChange(
                                          question.id,
                                          e.target.value
                                        )
                                      }
                                      style={{
                                        fontSize: "13px",
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "6px",
                                        backgroundColor: "#F9FAFB",
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </AccordionItemPanel>
                      </AccordionItem>
                    </div>}
                  </Accordion>
                </div>
              </div>
            )}
            {!isLoading && headings.length > 0 && !date && (
              <div className="noScroll h-90 verticalScroll pb-17">
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <h4 className="fs-18 text_primary text-center font-weight-bold mt-4 pt-2 mb-2">
                    Choose Assessment Date
                  </h4>
                  <div className="d-flex align-items-center justify-content-center mb-3">
                    <p className="text_small mb-0 text-center">
                      <span className="font-weight-bold">Note:</span> Choose the
                      date that you completed the assessment.
                    </p>
                    <div className="icon-container position-relative ml-1">
                      <sup>
                        <FaInfoCircle />
                      </sup>

                      <div className="overlay shadow rounded w-236">
                        <div
                          className="my-2 pl-2"
                          style={{ width: "100%", height: "100%" }}
                        >
                          <p>
                            The Assessment Date is the date that you SAW or
                            ASSESSED the patient. It is not always the date that
                            you enter the data.
                          </p>
                          <p>
                            eg. <br />
                            if you saw the patient on 10 Jan 2022 and you are
                            entering the data on 10 Jan 2022, select 10 Jan 2022
                            as the ASSESSMENT DATE.
                          </p>
                          <p>
                            if you saw the patient on 10 Jan 2022 and you are
                            entering the data on 12 Jan 2022, select 10 Jan 2022
                            as the ASSESSMENT DATE.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ width: "260px" }}>
                    <DatePicker
                      selected={chosenDate}
                      onChange={(selectedDate) => setChosenDate(selectedDate)}
                      customInput={<AssessmentDateInput />}
                      dateFormat="dd MMM yyyy"
                      maxDate={new Date()}
                    />
                    {chosenDate && (
                      <div className="d-flex justify-content-between mt-4">
                        <button
                          type="button"
                          className="btn btn_outline_danger w-116 rounded f-13 h-35"
                          onClick={() =>
                            props.history.push(
                              `/hcp/patient/details/${patient}`
                            )
                          }
                        >
                          BACK
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary w-116 rounded f-13 h-35"
                          onClick={() => setDate(chosenDate)}
                        >
                          CHOOSE
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isLoading && headings.length > 0 && date ? (
        <FooterButton
          child1={
            <Item1
              setIsOpen={setIsOpen}
              isOpen={isOpen}
              onClear={handleClearAction}
              onClearConfirm={handleClearAllScores}
            />
          }
          child2={
            <Item2
              props={props}
              scores={scores}
              questions={questions}
              date={date}
              handleSubmit={handleSubmit}
              isPending={isPending || addIsPending}
              onSaveForLater={handleSaveForLaterAction}
              onReview={handleReviewAction}
            />
          }
        />
      ) : (
        <Footer />
      )}
      <div
        className="modal fade"
        id="commentModal"
        tabIndex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content pt-3">
            <div className="modal-header border-0">
              <div className="flex-grow-1 text-center">
                <h5 className="text_primary" style={{ fontWeight: 600 }}>
                  Comments
                </h5>
              </div>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 40, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div className="modal-body mb-3">
              <div className="border rounded bg-light py-1 px-2">
                <textarea
                  maxLength={50}
                  className="form-control p-0 border-0 bg-light f-11 text-black-50"
                  placeholder="Write your Comments here!"
                  rows={7}
                  onChange={handleChange}
                  value={comment}
                />
                <small className="d-flex justify-content-end text-muted f-11">
                  {comment.length} / 50
                </small>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between align-items-center border-0">
              <button
                type="button"
                className="btn btn_outline_danger px-5"
                onClick={() => setComment("")}
              >
                CLEAR
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-5"
                onClick={handleAddComment}
              >
                COMMENT
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Confirmation Modal */}
      <div
        className={`modal fade ${showNavigationModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showNavigationModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header border-0 d-flex justify-content-center">
              <div className="text-center">
                <FaExclamationTriangle
                  size={40}
                  className="text-warning mb-2"
                />
                <h5 className="modal-title text_primary font-weight-bold">
                  Warning
                </h5>
              </div>
            </div>
            <div className="modal-body text-center px-4">
              <p className="text_default fs-13">
                Are you sure you want to leave this screen?
              </p>
              <p className="text_default fs-13">
                <b>Note:</b> All data will be lost
              </p>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between px-4 pb-4">
              <button
                type="button"
                className="btn btn_outline_danger px-4"
                onClick={handleCancelNavigation}
              >
                <small>GO BACK</small>
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={handleConfirmNavigation}
              >
                <small>YES</small>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FunctionalScore;
